/**
 * PortalMessageApi — one call: send the PASSENGER of a booking an ad-hoc
 * message, as the company, on WhatsApp or by email.
 *
 * Built on the portal's own client (`portalClient` + `payloadOf` from
 * PortalApi.js), so the portal token, the silent refresh and the "never send
 * the admin token" guarantee all come from there — the same footing as
 * DispatcherPortalApi and OperatorPortalApi.
 *
 *   POST /portal/message  { booking_id, text }                        WhatsApp
 *   POST /portal/message  { booking_id, channel: 'email', subject, text }
 *
 * AND NOTHING ELSE IN THE BODY, ON EITHER CHANNEL. The server derives the
 * RECIPIENT from the booking — the phone number, the greeting name and, for
 * email, the address — and a client can set none of them. That is the point:
 * a dispatcher cannot mistype a number or an address and reach a stranger,
 * and cannot address a passenger by somebody else's name. The portal is
 * never even told the passenger's email address, and the API REFUSES a
 * client-supplied one. So this sends a body, a subject on email, and the
 * booking it belongs to — nothing it might be tempted to "help" with.
 *
 * WHATSAPP SENDS NO `channel` AT ALL, and that is deliberate rather than an
 * omission. The API defaults an absent `channel` to `whatsapp`, so the body
 * on the live path is byte-for-byte what it was before email existed. Adding
 * a redundant `channel: 'whatsapp'` would have meant every WhatsApp send in
 * production depending on the new field being deployed first; this way the
 * channel appears on the wire only when it is actually carrying information.
 *
 * THE ONE-LINE RULE IS WHATSAPP'S, NOT THE ENDPOINT'S. A WhatsApp body goes
 * into the `{{2}}` slot of `we_have_a_message_for_you` and WhatsApp refuses a
 * line break inside a template variable, so it is flattened here (`oneLine`)
 * — the composer already prevents one being typed, and this is the boundary
 * making it true of anything that calls in. An EMAIL body has no such
 * constraint and is sent EXACTLY AS WRITTEN, paragraphs and all. Flattening
 * it would destroy the one thing email is better at for no reason at all.
 *
 * It lives in its own file, with one function, because the endpoint is new
 * and its path is the thing most likely to move: when it does, this is the
 * only file that changes.
 */
import { payloadOf, portalClient } from './PortalApi';
import { oneLine } from '../Utils/oneLine';
import { EMAIL, isEmailChannel } from '../Utils/messageChannel';

/**
 * The body for one send, built from scratch each time.
 *
 * Assembled rather than spread from the caller's argument on purpose: a
 * caller that one day passes `email`, `phone_number` or `first_name` must not
 * be able to leak it onto the wire by accident.
 */
const messageBody = ({
  bookingId, channel, subject, text,
}) => (isEmailChannel(channel)
  ? {
    booking_id: bookingId,
    channel: EMAIL,
    // Trimmed, not flattened: a subject is one line by nature, and the
    // composer will not arm the send without one.
    subject: oneLine(subject),
    // The paragraphs ARE the message. Nothing is collapsed here.
    text: String(text ?? ''),
  }
  : {
    booking_id: bookingId,
    text: oneLine(text),
  });

export const sendPassengerMessage = async (args) => payloadOf(
  await portalClient.post('/portal/message', messageBody(args || {})),
);

export default sendPassengerMessage;
